class ProductMetafieldGallery extends HTMLElement {
  connectedCallback() {
    this.mediaGallery = this.closest('media-gallery');
    this.thumbnails = Array.from(this.querySelectorAll('[data-product-metafield-gallery-thumb]'));
    this.previousButton = this.querySelector('[data-product-metafield-gallery-prev]');
    this.nextButton = this.querySelector('[data-product-metafield-gallery-next]');
    this.arrowVisibilityFrame = 0;
    this.arrowVisibilityTimeout = 0;

    if (!this.mediaGallery || this.thumbnails.length === 0) return;

    this.activeIndex = this.thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-selected') === 'true');
    if (this.activeIndex >= 0) {
      this.updateSelectedThumbnail(this.activeIndex);
    }

    this.addEventListener('click', this.handleClick);
    this.initSwiper();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    if (this.arrowVisibilityFrame) cancelAnimationFrame(this.arrowVisibilityFrame);
    if (this.arrowVisibilityTimeout) clearTimeout(this.arrowVisibilityTimeout);
    this.resizeObserver?.disconnect();
    this.swiper?.destroy(true, true);
  }

  initSwiper() {
    if (this.dataset.slider !== 'true' || !window.Swiper) return;

    const swiperElement = this.querySelector('[data-product-metafield-gallery-swiper]');

    if (!swiperElement) return;

    this.swiper = new window.Swiper(swiperElement, {
      allowTouchMove: true,
      focusableElements: 'input, select, option, textarea, video, label',
      grabCursor: true,
      preventClicks: true,
      preventClicksPropagation: true,
      simulateTouch: true,
      slidesPerView: 'auto',
      slidesPerGroupAuto: true,
      spaceBetween: 8,
      speed: 250,
      threshold: 4,
    });

    this.swiper.on('setTranslate', () => this.scheduleArrowVisibilityUpdate());
    this.swiper.on('slideChange', () => this.scheduleArrowVisibilityUpdate());
    this.swiper.on('transitionEnd', () => this.updateArrowVisibility());
    this.swiper.on('touchEnd', () => this.updateArrowVisibility());
    this.swiper.on('reachBeginning', () => this.updateArrowVisibility());
    this.swiper.on('reachEnd', () => this.updateArrowVisibility());
    this.swiper.on('fromEdge', () => this.updateArrowVisibility());
    this.swiper.on('resize', () => {
      this.swiper?.update();
      this.scheduleArrowVisibilityUpdate();
    });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.swiper?.update();
        this.scheduleArrowVisibilityUpdate();
      });
      this.resizeObserver.observe(swiperElement);
    }

    requestAnimationFrame(() => {
      this.swiper?.update();
      this.updateArrowVisibility();
    });
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const previous = event.target.closest('[data-product-metafield-gallery-prev]');
    const next = event.target.closest('[data-product-metafield-gallery-next]');

    if ((previous || next) && this.contains(previous || next)) {
      event.preventDefault();
      if ((previous || next).hasAttribute('disabled')) return;
      this.slideThumbnails(previous ? -1 : 1);
      return;
    }

    const thumbnail = event.target.closest('[data-product-metafield-gallery-thumb]');
    if (!thumbnail || !this.contains(thumbnail)) return;

    event.preventDefault();
    if (this.swiper && this.swiper.allowClick === false) return;

    const index = this.thumbnails.indexOf(thumbnail);
    this.setActiveIndex(index);
  };

  slideThumbnails(direction) {
    if (!this.swiper) return;

    if (direction < 0) {
      this.swiper.slidePrev();
      this.scheduleArrowVisibilityUpdate();
      this.schedulePostTransitionArrowVisibilityUpdate();
      return;
    }

    this.swiper.slideNext();
    this.scheduleArrowVisibilityUpdate();
    this.schedulePostTransitionArrowVisibilityUpdate();
  }

  setActiveIndex(index, options = {}) {
    if (index < 0 || index >= this.thumbnails.length) return;

    const { updateMain = true, sync = true, slide = true } = options;
    const thumbnail = this.thumbnails[index];
    if (!thumbnail) return;

    this.activeIndex = index;
    this.updateSelectedThumbnail(index);

    if (slide && this.swiper && this.swiper.activeIndex !== index) {
      this.swiper.slideTo(index);
      this.schedulePostTransitionArrowVisibilityUpdate();
    }

    this.scheduleArrowVisibilityUpdate();

    if (updateMain) {
      this.updateMainMedia(thumbnail);
    }

    if (sync) {
      this.syncSiblingGalleries(index);
    }
  }

  updateSelectedThumbnail(index) {
    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const isSelected = thumbnailIndex === index;
      thumbnail.setAttribute('aria-selected', `${isSelected}`);
    });
  }

  updateArrowVisibility() {
    if (!this.previousButton || !this.nextButton || !this.swiper) return;

    const { atBeginning, atEnd, hasOverflow } = this.getSwiperPositionState();

    this.setArrowHidden(this.previousButton, !hasOverflow || atBeginning);
    this.setArrowHidden(this.nextButton, !hasOverflow || atEnd);
  }

  scheduleArrowVisibilityUpdate() {
    if (this.arrowVisibilityFrame) cancelAnimationFrame(this.arrowVisibilityFrame);

    this.arrowVisibilityFrame = requestAnimationFrame(() => {
      this.arrowVisibilityFrame = 0;
      this.updateArrowVisibility();
    });
  }

  schedulePostTransitionArrowVisibilityUpdate() {
    if (this.arrowVisibilityTimeout) clearTimeout(this.arrowVisibilityTimeout);

    const speed = Number(this.swiper?.params?.speed) || 250;
    this.arrowVisibilityTimeout = setTimeout(() => {
      this.arrowVisibilityTimeout = 0;
      this.updateArrowVisibility();
    }, speed + 50);
  }

  getSwiperPositionState() {
    if (!this.swiper) return { atBeginning: true, atEnd: true, hasOverflow: false };

    const tolerance = 1;
    const currentTranslate = this.swiper.getTranslate();
    const minTranslate = this.swiper.minTranslate();
    const maxTranslate = this.swiper.maxTranslate();
    const swiperSize = this.swiper.size || this.swiper.el?.clientWidth || 0;
    const virtualSize = this.swiper.virtualSize || 0;

    const swiperElement = this.swiper.el || this.querySelector('[data-product-metafield-gallery-swiper]');
    const wrapper = this.swiper.wrapperEl || swiperElement?.querySelector('.swiper-wrapper');
    const canMeasureOverflow = Boolean(swiperElement && wrapper);
    const hasMeasuredOverflow = Boolean(
      swiperElement &&
        wrapper &&
        wrapper.scrollWidth > swiperElement.clientWidth + tolerance
    );
    const hasVirtualOverflow = Boolean(swiperSize && virtualSize && virtualSize > swiperSize + tolerance);
    const hasTranslateOverflow = Math.abs(maxTranslate - minTranslate) > tolerance;

    return {
      atBeginning: currentTranslate >= minTranslate - tolerance,
      atEnd: currentTranslate <= maxTranslate + tolerance,
      hasOverflow: canMeasureOverflow ? hasMeasuredOverflow : hasVirtualOverflow || hasTranslateOverflow,
    };
  }

  setArrowHidden(button, hidden) {
    button.classList.toggle('tw-hidden', hidden);
    button.toggleAttribute('disabled', hidden);
    button.setAttribute('aria-hidden', `${hidden}`);
  }

  syncSiblingGalleries(index) {
    const galleries = this.mediaGallery.querySelectorAll('product-metafield-gallery');
    galleries.forEach((gallery) => {
      if (gallery === this || typeof gallery.setActiveIndex !== 'function') return;
      gallery.setActiveIndex(index, { updateMain: false, sync: false });
    });
  }

  updateMainMedia(thumbnail) {
    const targets = [
      this.mediaGallery.querySelector('.media-gallery__grid > .product-media-container:first-child'),
      this.mediaGallery.querySelector('slideshow-component slideshow-slide.product-media-container:first-child'),
    ].filter(Boolean);

    targets.forEach((target) => this.updateMediaTarget(target, thumbnail));
  }

  updateMediaTarget(target, thumbnail) {
    const image = target.querySelector('img.product-media__image');
    const productMedia = target.querySelector('.product-media');

    if (!image) return;

    const {
      mainSrc,
      fullSrc,
      srcset,
      alt,
      aspectRatio,
      width,
      height,
      zoomIndex,
      mediaId,
    } = thumbnail.dataset;

    if (mainSrc) image.src = mainSrc;
    if (srcset) image.srcset = srcset;
    if (alt !== undefined) image.alt = alt;
    if (width) image.width = Number(width);
    if (height) image.height = Number(height);
    if (fullSrc) {
      image.setAttribute('data_max_resolution', fullSrc);
      image.setAttribute('data-max-resolution', fullSrc);
      image.dataset.maxResolution = fullSrc;
    }

    if (aspectRatio) {
      target.style.setProperty('--media-preview-ratio', aspectRatio);
      productMedia?.style.setProperty('--ratio', aspectRatio);
    }

    if (productMedia) {
      const nextMediaId = mediaId || (zoomIndex ? `metafield-${zoomIndex}` : '');
      if (nextMediaId) {
        productMedia.setAttribute('data-media-id', nextMediaId);
      }
    }

    if (zoomIndex) {
      const zoomButton = target.querySelector('[on\\:click]');
      if (zoomButton) {
        zoomButton.setAttribute('on:click', `/zoom/${zoomIndex}`);
      }

      if (target.tagName.toLowerCase() === 'slideshow-slide') {
        const zoomDialog = this.mediaGallery.querySelector('zoom-dialog[id]');
        if (zoomDialog) {
          target.setAttribute('on:click', `#${zoomDialog.id}/open/${zoomIndex}`);
        }
      }
    }
  }
}

if (!customElements.get('product-metafield-gallery')) {
  customElements.define('product-metafield-gallery', ProductMetafieldGallery);
}
